/**
 * Stream overlay (OBS-friendly, transparent background).
 *
 * Layout: bottom band with team-standings table on the left, current-player
 * bid card centered, and player info card on the right. Center-top has the
 * NitiSoft watermark (no dark card so it composites cleanly over the video).
 * SaleBanner respects the organizer's custom SOLD / UNSOLD media.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { resolveImageUrl } from "@/components/ImageInput";
import {
  PHOTO_FALLBACK, SaleBanner, useSaleAnnouncement, usePublicAuction, useOverlayState,
  FIELD_LABELS, readPlayerField, getLedDisplayFields, useAccentStyle, brandLogoFor,
} from "./_shared";

const STANDINGS_PAGE_SIZE = 6;   // rows visible at a time on the stream
const STANDINGS_ROTATE_MS = 5000; // auto-rotate every 5 s when >6 teams

export function OverlayStream() {
  const { auctionId } = useParams();
  const { auction, players, teams, reload } = usePublicAuction(auctionId);
  const state = useOverlayState(auctionId, reload);

  const cur = state?.current_player;
  const bidder = state?.current_bidder_team;
  const announce = useSaleAnnouncement(auctionId, players, teams);
  const ledExtraFields = getLedDisplayFields(auction);
  const accentStyle = useAccentStyle(auction);

  const fullStandings = useMemo(() => {
    return teams
      .map((t) => {
        const bought = players.filter((p) => p.sold_to === t.id);
        return {
          ...t,
          pl: bought.length,
          points: bought.reduce((s, p) => s + (p.sold_price || 0), 0),
        };
      })
      .sort((a, b) => b.points - a.points);
  }, [teams, players]);

  // Rotating window — if more than 6 teams, auto-shift the visible window every
  // 5 s so every team's balance appears on the stream during a live auction.
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(fullStandings.length / STANDINGS_PAGE_SIZE));
  useEffect(() => {
    if (fullStandings.length <= STANDINGS_PAGE_SIZE) { setPage(0); return; }
    const id = setInterval(() => {
      setPage((p) => (p + 1) % totalPages);
    }, STANDINGS_ROTATE_MS);
    return () => clearInterval(id);
  }, [fullStandings.length, totalPages]);
  useEffect(() => { if (page >= totalPages) setPage(0); }, [totalPages, page]);

  const standings = useMemo(() => {
    const start = page * STANDINGS_PAGE_SIZE;
    return fullStandings.slice(start, start + STANDINGS_PAGE_SIZE);
  }, [fullStandings, page]);

  return (
    <div className="min-h-screen relative" style={{ background: "transparent", fontFamily: "Inter, sans-serif", ...accentStyle }} data-testid="overlay-stream">
      <SaleBanner announce={announce} auction={auction} position="bottom" />
      <style>{`html, body, #root { background: transparent !important; }`}</style>

      {/* Center-top logo watermark — no dark card, sits directly on the stream */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center pointer-events-none">
        <img src={brandLogoFor(auction)} alt="" className="h-10 drop-shadow-2xl" />
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-3 flex items-end justify-between gap-2 z-20">
        {standings.length > 0 && (
          <div
            className="rounded-lg shadow-2xl overflow-hidden border-2 border-orange-500/70 min-w-[280px]"
            style={{ background: "linear-gradient(180deg,rgba(0,0,0,0.85),rgba(20,0,0,0.9))" }}
            data-testid="stream-standings"
          >
            <table className="w-full text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
              <thead>
                <tr className="text-white uppercase text-[10px] tracking-widest" style={{ background: "linear-gradient(180deg,var(--accent-color),var(--accent-mid))" }}>
                  <th className="px-3 py-1 text-left font-bold">
                    Team
                    {totalPages > 1 && (
                      <span className="ml-2 text-amber-300 font-mono" data-testid="stream-standings-page">{page + 1}/{totalPages}</span>
                    )}
                  </th>
                  <th className="px-2 py-1 text-center font-bold">PL</th>
                  <th className="px-2 py-1 text-right font-bold">Balance</th>
                  <th className="px-3 py-1 text-right font-bold">Spent</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((t) => (
                  <tr key={t.id} className="border-b border-orange-500/20 last:border-0" data-testid={`stream-standings-row-${t.id}`}>
                    <td className="px-3 py-1 text-white font-bold uppercase truncate">{t.name}</td>
                    <td className="px-2 py-1 text-center font-mono text-yellow-300">{t.pl}</td>
                    <td className="px-2 py-1 text-right font-mono text-emerald-300 font-bold">{(t.remaining_budget != null ? t.remaining_budget : ((t.budget || 0) - (t.points || 0))).toLocaleString()}</td>
                    <td className="px-3 py-1 text-right font-mono text-yellow-400 font-bold">{t.points.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cur && (
          <div
            className="rounded-lg overflow-visible shadow-2xl border-2 border-orange-500/70 flex flex-col items-center px-4 pt-3 pb-2 relative"
            style={{ background: "linear-gradient(140deg,var(--accent-mid),var(--accent-color))", minWidth: 260, fontFamily: "Inter, sans-serif" }}
            data-testid="stream-current-bid-card"
          >
            <div className="-mt-16 mb-1 relative">
              <div className="w-28 h-32 rounded-xl bg-red-900 border-[3px] border-yellow-400 shadow-2xl overflow-hidden">
                <img
                  src={cur.photo_url ? resolveImageUrl(cur.photo_url) : PHOTO_FALLBACK}
                  onError={(e) => { e.target.src = PHOTO_FALLBACK; }}
                  alt=""
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div
              className="text-white text-lg sm:text-xl font-black uppercase tracking-wide text-center leading-tight mb-1 px-1"
              style={{ textShadow: "0 2px 6px rgba(0,0,0,0.85), 0 0 3px rgba(0,0,0,0.9)" }}
              data-testid="stream-player-name"
            >
              {cur.name}
            </div>
            <div className="text-yellow-300 text-[10px] uppercase font-bold tracking-widest">CURRENT BID</div>
            <div className="font-black text-4xl leading-none text-yellow-400 drop-shadow-lg" data-testid="stream-current-bid">
              {(state?.current_bid || 0).toLocaleString()}
            </div>
            {bidder && (
              <div className="mt-0.5 flex items-center gap-1 text-white text-[11px] uppercase tracking-wider font-bold" data-testid="stream-current-bidder">
                {bidder.logo_url && (
                  <img src={resolveImageUrl(bidder.logo_url)} alt="" className="w-4 h-4 rounded object-cover" />
                )}
                {bidder.name}
              </div>
            )}
          </div>
        )}

        {cur ? (
          <div
            className="rounded-lg overflow-hidden shadow-2xl border-2 border-orange-500/70"
            style={{ background: "linear-gradient(140deg,var(--accent-mid),var(--accent-color))", minWidth: 220, maxWidth: 320, fontFamily: "Inter, sans-serif" }}
            data-testid="stream-player-card"
          >
            <div
              className="px-4 py-2 text-center font-black uppercase text-white text-base tracking-wide"
              style={{ background: "linear-gradient(180deg,var(--accent-color),var(--accent-mid))", textShadow: "0 2px 6px rgba(0,0,0,0.85), 0 0 3px rgba(0,0,0,0.9)" }}
            >
              {cur.name}
              <div className="text-[10px] font-semibold opacity-90">{cur.role}</div>
            </div>
            <div className="px-4 py-2 bg-black/30 flex items-center justify-between text-white">
              <span className="text-xs uppercase font-bold tracking-wider">BASE VALUE</span>
              <span className="font-black text-lg text-yellow-300">{(cur.base_price || 0).toLocaleString()}</span>
            </div>
            {ledExtraFields.length > 0 && (
              <div className="px-4 py-2 bg-black/20 border-t border-orange-500/20 text-white text-[11px] grid grid-cols-2 gap-x-3 gap-y-0.5" data-testid="stream-extra-stats">
                {ledExtraFields.slice(0, 6).map((k) => {
                  const v = readPlayerField(cur, k);
                  if (v == null || v === "") return null;
                  return (
                    <div key={k} className="truncate" data-testid={`stream-extra-${k}`}>
                      <span className="text-orange-300 font-bold mr-1">{FIELD_LABELS[k] || k.toUpperCase()}</span>{v}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg bg-black/60 border border-white/20 px-4 py-3 text-white text-sm uppercase tracking-widest">
            No active player
          </div>
        )}
      </div>
    </div>
  );
}
